import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../i18n/resources';

const LANGUAGE_STORAGE_KEY = 'restaurant-manager::language-preference';

const isBrowser = () =>
  typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && typeof document !== 'undefined';

export const normalizeLanguage = (value) => {
  if (typeof value !== 'string') {
    return DEFAULT_LANGUAGE;
  }

  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return DEFAULT_LANGUAGE;
  }

  if (SUPPORTED_LANGUAGES.includes(trimmed)) {
    return trimmed;
  }

  const matchedPrefix = SUPPORTED_LANGUAGES.find((language) => trimmed.startsWith(`${language}-`));
  if (matchedPrefix) {
    return matchedPrefix;
  }

  return DEFAULT_LANGUAGE;
};

export const detectBrowserLanguage = () => {
  if (!isBrowser()) {
    return DEFAULT_LANGUAGE;
  }

  const candidates = [];

  if (Array.isArray(navigator.languages)) {
    candidates.push(...navigator.languages);
  }

  if (typeof navigator.language === 'string') {
    candidates.push(navigator.language);
  }

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const normalized = normalizeLanguage(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return DEFAULT_LANGUAGE;
};

export const getStoredLanguagePreference = () => {
  if (!isBrowser()) {
    return DEFAULT_LANGUAGE;
  }

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (typeof stored === 'string' && stored) {
      return normalizeLanguage(stored);
    }
  } catch {
    // Ignore storage access errors
  }

  return detectBrowserLanguage();
};

export const setStoredLanguagePreference = (language) => {
  if (!isBrowser()) {
    return;
  }

  const normalized = normalizeLanguage(language);

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage access errors
  }
};

export const applyLanguagePreference = (language) => {
  if (!isBrowser()) {
    return DEFAULT_LANGUAGE;
  }

  const normalized = normalizeLanguage(language);
  document.documentElement.lang = normalized;
  return normalized;
};

export const initializeLanguagePreference = () => {
  const preference = getStoredLanguagePreference();
  const applied = applyLanguagePreference(preference);
  setStoredLanguagePreference(applied);
  return applied;
};

export default {
  normalizeLanguage,
  detectBrowserLanguage,
  getStoredLanguagePreference,
  setStoredLanguagePreference,
  applyLanguagePreference,
  initializeLanguagePreference,
};
